import type { NotificationModule } from '@/services/notificationsService'

/**
 * En qué módulo está trabajando el usuario, cuando la pantalla no lo dice.
 *
 * La regla vivía suelta dentro del dashboard, que es quien decide si pinta el
 * panel de Almacén, el de Proveedores o el de RH. La campanita tomaba su propia
 * decisión, y las dos se salían: en una cuenta sin módulo activo configurado el
 * dashboard mostraba Almacén —porque el usuario tiene permisos de almacén— y la
 * campanita filtraba por RH, así que no le mostraba nada de lo que estaba
 * viendo. Aquí vive una sola vez para que no puedan volver a discrepar.
 */

export interface QuienEs {
  /** `key` del módulo activo del perfil, si se pudo resolver. */
  claveDelModuloActivo?: string | null
  esMaster?: boolean
  permisos?: string[]
}

/**
 * Sin módulo activo configurado, tener permisos de almacén se toma como estar
 * en almacén: es la cuenta que nunca eligió módulo y trabaja ahí.
 */
export function tieneAccesoAAlmacen({ esMaster, permisos }: QuienEs): boolean {
  if (esMaster) return true

  return (permisos ?? []).some(
    (p) => p === 'almacen.inicio.dashboard.ver' || p.startsWith('almacen.'),
  )
}

/** El módulo del usuario. RH es el resto del sistema, y por eso el último. */
export function moduloDelUsuario(quien: QuienEs): NotificationModule {
  const clave = quien.claveDelModuloActivo

  if (clave === 'mod_proveedores') return 'suppliers'
  if (clave === 'mod_almacen' || clave === 'almacen') return 'almacen'
  if (!clave && tieneAccesoAAlmacen(quien)) return 'almacen'

  return 'rh'
}
